/**
 * Persistent cache for schedule, dashboard, tournament list, feed.
 * Uses localStorage with fallback to in-memory. Reduces backend calls and enables offline-first.
 * Each entry: { data, asOf, fetchedAt }. asOf = server timestamp (ms) for delta/conflict checks.
 */

const PREFIX = 'tt_cache_';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min – use cache for instant load, refresh in background after this
const COMPLETED_MATCH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days – any completed match data; no refetch until expiry (profile, statistics)
const STATISTICS_TTL_MS = COMPLETED_MATCH_TTL_MS; // statistics = completed match data
const PLAYER_PROFILE_TTL_MS = COMPLETED_MATCH_TTL_MS;
const FULL_REFRESH_TTL_MS = 24 * 60 * 60 * 1000; // 24h – periodic full refresh for schedule/dashboard to avoid long-term drift
const memoryFallback = new Map();

function storageKey(key) {
  return `${PREFIX}${key}`;
}

function isExpired(entry, ttlMs = CACHE_TTL_MS) {
  if (!entry?.fetchedAt) return true;
  return Date.now() - entry.fetchedAt > ttlMs;
}

export function getCached(key) {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (raw) {
      const entry = JSON.parse(raw);
      return entry;
    }
  } catch (e) {
    // quota or parse error
  }
  return memoryFallback.get(key) || null;
}

export function setCached(key, data, asOf) {
  const entry = { data, asOf: asOf ?? Date.now(), fetchedAt: Date.now() };
  try {
    localStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch (e) {
    memoryFallback.set(key, entry);
  }
}

export function getCachedData(key) {
  const entry = getCached(key);
  return entry?.data ?? null;
}

export function getCacheAsOf(key) {
  const entry = getCached(key);
  return entry?.asOf ?? null;
}

export function isCacheStale(key, ttlMs) {
  const entry = getCached(key);
  return !entry || isExpired(entry, ttlMs ?? getCacheTtlMs(key));
}

/** True if cache exists but is older than maxAgeMs (e.g. 24h for periodic full refresh). */
export function isCacheOlderThan(key, maxAgeMs) {
  const entry = getCached(key);
  return entry?.data != null && entry?.fetchedAt != null && Date.now() - entry.fetchedAt > maxAgeMs;
}

export function invalidateCache(key) {
  try {
    localStorage.removeItem(storageKey(key));
  } catch (_) {}
  memoryFallback.delete(key);
}

/** Invalidate both statistics rankings and recent caches for a game so next load gets fresh data after any match update. */
export function invalidateStatisticsCache(gameId) {
  invalidateCache(CACHE_KEYS.statistics(gameId, 'rankings'));
  invalidateCache(CACHE_KEYS.statistics(gameId, 'recent'));
}

/** Invalidate cached player profile (completed match data) for a user; call after completing a match they were in. */
export function invalidatePlayerProfileCache(userId) {
  if (userId) invalidateCache(CACHE_KEYS.playerProfile(userId));
}

/**
 * Merge updates into cached data via a patcher function. Used to update individual
 * entities (e.g. a match) after backend mutation instead of invalidating the whole cache.
 * @param {string} key - Cache key
 * @param {function(data: object): object} patcher - Receives current cached data, returns new data to store
 */
export function patchCached(key, patcher) {
  const entry = getCached(key);
  if (!entry?.data) return;
  const patched = patcher(entry.data);
  if (patched != null) setCached(key, patched, entry.asOf);
}

export const CACHE_KEYS = {
  schedule: (gameId) => `schedule_${gameId}`,
  dashboard: (gameId) => `dashboard_${gameId}`,
  tournamentList: (gameId) => `tournamentList_${gameId}`,
  feed: (gameId) => `feed_${gameId}`,
  statistics: (gameId, part) => (part ? `statistics_${gameId}_${part}` : `statistics_${gameId}`),
  playerProfile: (userId) => `playerProfile_${userId}`,
  landing: () => 'landing',
};

export const CACHE_TTL_MS_EXPORT = CACHE_TTL_MS;
export const STATISTICS_TTL_MS_EXPORT = STATISTICS_TTL_MS;
export const PLAYER_PROFILE_TTL_MS_EXPORT = PLAYER_PROFILE_TTL_MS;
export const FULL_REFRESH_TTL_MS_EXPORT = FULL_REFRESH_TTL_MS;

/** TTL in ms for a cache key (statistics and player profile = 30d completed-match data; no refetch). */
export function getCacheTtlMs(key) {
  if (key && String(key).startsWith('playerProfile_')) return PLAYER_PROFILE_TTL_MS;
  if (key && String(key).startsWith('statistics_')) return STATISTICS_TTL_MS;
  return CACHE_TTL_MS;
}
